<template>
  <jet-dialog-modal icon="fas fa-user" id="planRequestModal" bodyClasses="p-0">
    <template #title>
      <template v-if="request">
        <span v-if="request.type == 'balance'">
          Account Balance Request <span v-if="request.user.first_name">- {{ request.user.first_name }} {{ request.user.last_name }}</span>
        </span>
        <span v-else-if="request.type == 'cash'">
          Cash Payment Confirmation <span v-if="request.user.first_name">- {{ request.user.first_name }} {{ request.user.last_name }}</span>
        </span>
        <span v-else>
          Payment Term Request <span v-if="request.user.first_name">- {{ request.user.first_name }} {{ request.user.last_name }}</span>
        </span>
      </template>
    </template>
    <template #content>
      <div v-if="request">
        <div class="px-3 pt-3">
          <div v-if="request.type == 'cash'">
            <p class="text-size-small text-secondary alert alert-info">
              User {{ request.user.first_name }} {{ request.user.last_name }} is paying cash for order of $<span class="text-success">{{ formatCurrency(request.order_total) }}</span>
            </p>
          </div>
          <div v-else-if="request.type == 'balance'">
            <p class="text-size-small lh-regular">
              User {{ request.user.first_name }} {{ request.user.last_name }} requested to add order amount <span class="text-success">{{ formatDollars(request.order_total) }}</span> to their account balance.
            </p>

            <div class="mb-2 d-flex flex-column flex-lg-row align-items-start">
              <p v-if="request.user.email" class="mb-2 text-secondary mr-3">
                <i class="fas fa-envelope"></i> <Email v-model="request.user.email" />
              </p>
              <p v-if="request.user.phone_number" class="mb-2 text-secondary">
                <i class="fas fa-phone-alt"></i> <Phone v-model="request.user.phone_number" />
              </p>
            </div>

            <div class="row mt-2 pt-3 border-top">
              <div class="col-12 col-sm-6 mb-3 mb-lg-0">
                <p class="mb-1 font-weight-bold">Current Account Balance</p>
                <h5 class="mb-0">{{ request.user.account_balance < 0 ? '-$' : '$' + formatCurrency(Math.abs(request.user.account_balance)) }}</h5>
              </div>
              <div class="col-12 col-sm-6">
                <p class="mb-1 font-weight-bold">Minimum Payment</p>
                <AmountInput v-model="formMinimum.payment_minimum" @change="updateMinimum"/>
                <span class="ml-2" v-if="minimumSet"><i class="fa fa-check-circle text-success"></i></span>
              </div>
            </div>
          </div>
          <div v-else>
            <p class="text-size-small lh-regular">
              User {{ request.user.first_name }} {{ request.user.last_name }} requested {{ request.plan.weeks }} weekly payments of <span class="text-success">{{ formatDollars(request.plan.payment_amount) }}</span>.
            </p>
          </div>
          <div class="mt-4">
            <div class="cart-item" v-for="(item, index) in request.cart_items" :key="item.product.id">
              <ProductRow 
                icon="fas fa-shopping-basket"
                :itemName="item.product.name"
                :item="item"
                :margin="`${index+1 === request.cart_items.length ? 'mb-0' : 'mb-3'}`"
                :imgUrl="item.product.image_url"
                productStyle="compact"
                :sku="item.product.sku">
                  <p class="mb-0 text-secondary"><span class="text-success">{{ formatDollars(item.product.price) }}</span> x {{ item.qty }}</p>

                  <template v-slot:priceInfo>
                    <h5 class="text-success mb-0 mt-0 mt-lg-2">{{ formatDollars(item.product.price * item.qty) }}</h5>
                  </template>
              </ProductRow>          
            </div>
          </div>
        </div>

        <div class="p-3 bg-lighter">
          <ul class="list-unstyled">
            <li class="d-flex justify-content-between">Subtotal: <strong>{{ formatDollars(request.order_sub_total) }}</strong></li>
            <li v-if="request.shipping_cost > 0" class="d-flex justify-content-between">Shipping: <strong>{{ formatDollars(request.shipping_cost) }}</strong></li>
            <li class="d-flex justify-content-between">Sales Tax: 
              <strong v-if="request.sales_tax > 0">{{ formatDollars(request.sales_tax) }}</strong>
              <span v-else>N/A</span>
            </li>
          </ul>

          <p class="mb-0 d-flex justify-content-between align-items-end" v-if="request.user.available_credit">Account Credit Applied: <strong>{{ formatDollars(request.user.available_credit) }}</strong></p>
          
          <p class="mb-0 d-flex justify-content-between align-items-end">Total: <strong class="price-total">{{ formatDollars(request.order_total) }}</strong></p>
          
          <div v-if="declineProvideReason" class="mt-3">
            <textarea :value="declineReasonText" class="form-control" v-focus placeholder="Message to customer" id="decline_reason"></textarea>
          </div>

          <div v-else class="mt-3">
            <jet-button icon="fas fa-shopping-cart" @click="checkoutRequest">Checkout with this Cart</jet-button>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <jet-button icon="fas fa-clipboard-check" @click="approveRequest">Approve</jet-button>
      <jet-danger-button class="ml-2" @click="declineRequest">Decline</jet-danger-button>
    </template>
  </jet-dialog-modal>
</template>

<script>
  import JetDialogModal from '@/Jetstream/DialogModal'
  import JetDangerButton from '@/Jetstream/DangerButton'
  import ProductRow from '@/Components/Shop/ProductRow'
  import AmountInput from '@/Shared/AmountInput'

  export default {
    emits: ['approveRequest', 'declineRequest', 'update:declineReason'],
    data() {
      return {
        minimumSet: this.saved_minimum,
        declineReasonText: '',
      }
    },
    components: {
      JetDialogModal,
      JetDangerButton,
      ProductRow,
      AmountInput
    },
    props: {
      request: Object,
      formMinimum: Object,
      saved_minimum: Boolean,
      declineReason: String,
      declineProvideReason: Boolean
    },
    watch: {
      declineReason(val) {
        this.declineReasonText = val
      },
      declineReasonText(val) {
        this.$emit('update:declineReason', val)
      },
    },
    methods: {
      approveRequest() {
        this.$emit('approveRequest')
      },
      declineRequest() {
        this.$emit('declineRequest', this.declineReasonText)
      },
      checkoutRequest() {
        $('#planRequestModal').modal('hide');
        this.$inertia.visit(route('cart.checkfromrequest', this.request.id));
      },
      updateMinimum() {
        this.formMinimum.post(this.route('user.saveMinimumPayment', this.request.user))
        this.minimumSet = true
      },
    },
    directives: {
      focus: {
        // directive definition
        mounted(el) {
          el.focus()
          el.select()
        }
      }
    },
  }
</script>

<style scoped>
  .price-total {
    font-size: 1.15rem;
  }
</style>

